import './index.css';
import type { ThemeConfig } from '../../themes/types';

export interface BottomDockPanelOptions {
    id: string;
    content: HTMLElement;
    closable?: boolean;
    onClose: (id: string) => void;
}

interface BottomDockPanelInstance {
    id: string;
    element: HTMLElement;
    bodyElement: HTMLElement;
    closeButton: HTMLButtonElement | null;
    leaving: boolean;
    removeTimer: number | null;
}

export class BottomDockStack {
    private readonly container: HTMLElement;
    private readonly root: HTMLElement;
    private readonly panelGap = 10;
    private readonly panelMap: Map<string, BottomDockPanelInstance> = new Map();
    private readonly panelOrder: string[] = [];
    private readonly resizeObserver: ResizeObserver | null;
    private readonly handleWindowResize = () => {
        this.reflow();
    };

    constructor(container: HTMLElement) {
        this.container = container;
        this.root = document.createElement('div');
        this.root.className = 'bottom-dock-stack';
        this.container.appendChild(this.root);

        this.resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => {
                this.reflow();
            })
            : null;

        if (!this.resizeObserver) {
            window.addEventListener('resize', this.handleWindowResize);
        }
    }

    public setTheme(theme: ThemeConfig): void {
        const style = this.root.style;
        style.setProperty('--bd-bg', theme.floatingBtnBg);
        style.setProperty('--bd-border', theme.floatingBtnBorder);
        style.setProperty('--bd-shadow', theme.floatingBtnShadow);
        style.setProperty('--bd-close-color', theme.floatingIconColor);
        style.setProperty('--bd-close-color-hover', theme.floatingIconColorHover);
        style.setProperty('--bd-close-bg-hover', theme.floatingBtnBgHover);
        style.setProperty('--bd-close-border-hover', theme.floatingBtnBorder);
        style.setProperty('--bd-body-bg', theme.floatingBtnBg);
        style.setProperty('--bd-placeholder-border', theme.borderSubtle);
        style.setProperty('--bd-placeholder-text', theme.textSecondary);
    }

    public hasPanel(id: string): boolean {
        return this.panelMap.has(id);
    }

    public addPanel(options: BottomDockPanelOptions): void {
        const existing = this.panelMap.get(options.id);
        if (existing && existing.leaving) {
            existing.leaving = false;
            existing.element.classList.remove('is-leaving');
            if (existing.removeTimer) {
                clearTimeout(existing.removeTimer);
                existing.removeTimer = null;
            }
            existing.bodyElement.replaceChildren(options.content);
            if (!this.panelOrder.includes(options.id)) {
                this.panelOrder.push(options.id);
            }
            this.reflow();
            return;
        }

        if (existing) {
            return;
        }

        const panel = document.createElement('section');
        panel.className = 'bottom-dock-panel is-entering';
        panel.dataset.panelId = options.id;

        let closeButton: HTMLButtonElement | null = null;
        if (options.closable !== false) {
            closeButton = document.createElement('button');
            closeButton.className = 'bottom-dock-panel-close';
            closeButton.type = 'button';
            closeButton.setAttribute('aria-label', 'close-panel');
            closeButton.textContent = '×';
            closeButton.addEventListener('click', (event) => {
                event.stopPropagation();
                options.onClose(options.id);
            });
            panel.appendChild(closeButton);
        }

        const body = document.createElement('div');
        body.className = 'bottom-dock-panel-body';
        body.appendChild(options.content);

        panel.appendChild(body);
        this.root.appendChild(panel);

        this.panelOrder.push(options.id);
        this.panelMap.set(options.id, {
            id: options.id,
            element: panel,
            bodyElement: body,
            closeButton,
            leaving: false,
            removeTimer: null
        });

        this.resizeObserver?.observe(panel);

        this.reflow();
        requestAnimationFrame(() => {
            panel.classList.remove('is-entering');
        });
    }

    public removePanel(id: string): void {
        const panel = this.panelMap.get(id);
        if (!panel || panel.leaving) {
            return;
        }

        panel.leaving = true;
        panel.element.classList.add('is-leaving');

        const orderIndex = this.panelOrder.indexOf(id);
        if (orderIndex >= 0) {
            this.panelOrder.splice(orderIndex, 1);
        }

        this.reflow();
        panel.removeTimer = window.setTimeout(() => {
            this.resizeObserver?.unobserve(panel.element);
            panel.element.remove();
            this.panelMap.delete(id);
            panel.removeTimer = null;
        }, 220);
    }

    public createPlaceholderContent(text: string): HTMLElement {
        const el = document.createElement('div');
        el.className = 'bottom-dock-placeholder';
        el.textContent = text;
        return el;
    }

    public destroy(): void {
        this.resizeObserver?.disconnect();
        window.removeEventListener('resize', this.handleWindowResize);
        this.panelMap.clear();
        this.panelOrder.length = 0;
        this.root.remove();
    }

    private reflow(): void {
        let offset = 0;
        let maxWidth = 0;

        this.panelOrder.forEach((id) => {
            const panel = this.panelMap.get(id);
            if (!panel) {
                return;
            }

            panel.element.style.transform = `translateX(-50%) translateY(-${offset}px)`;
            offset += panel.element.offsetHeight + this.panelGap;
            maxWidth = Math.max(maxWidth, panel.element.offsetWidth);
        });

        const panelCount = this.panelOrder.length;
        const totalHeight = panelCount > 0 ? offset - this.panelGap : 0;
        this.root.style.height = `${totalHeight}px`;
        this.root.style.width = panelCount > 0 ? `${maxWidth}px` : '0px';
    }
}
